import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  Box,
  Button,
  EmptyState,
  FeatureGate,
  Screen,
  SelectableInstallmentRow,
  Text,
} from '~/atomic';
import { cn } from '~/utils/cn';
import modalStatusService from '~/services/modalStatus';
import { useFeature } from '~/store/entitlements/useFeature';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  selectAttentionAll,
  selectContractById,
  selectContracts,
  selectInstallmentsForContract,
  selectOverdueAttention,
} from '~/store/contracts/selectors';
import {
  fetchContractDetails,
  fetchContracts,
  markInstallmentsPaid,
  markInstallmentsPaidByContract,
} from '~/store/contracts/thunks';
import type { ContractInstallment } from '~/store/contracts/interfaces';
import { formatCentsToBRL } from '~/utils/formatFinance';

type AttentionFilter = 'all' | 'overdue' | 'soon';

interface Row {
  installment: ContractInstallment;
  contractLabel?: string;
}

export default function BulkPayInstallmentsView() {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isAttentionMode = !id;
  const attentionFilter = (searchParams.get('filter') ??
    'all') as AttentionFilter;

  const { isAvailable, isQuotaExhausted } = useFeature('installments_pay');
  const canPay = isAvailable && !isQuotaExhausted;

  const { saving } = useAppSelector(selectContracts);
  const contract = useAppSelector(selectContractById(id ?? ''));
  const contractInstallments = useAppSelector(
    selectInstallmentsForContract(id ?? ''),
  );
  const attentionAll = useAppSelector(selectAttentionAll);
  const attentionOverdue = useAppSelector(selectOverdueAttention);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (isAttentionMode) {
      if (attentionAll.length === 0) dispatch(fetchContracts());
      return;
    }
    if (id && !contractInstallments.length) dispatch(fetchContractDetails(id));
  }, [
    dispatch,
    id,
    isAttentionMode,
    attentionAll.length,
    contractInstallments.length,
  ]);

  const attentionItems = useMemo(() => {
    if (attentionFilter === 'overdue') return attentionOverdue;
    if (attentionFilter === 'soon')
      return attentionAll.filter((i) => i.urgency !== 'overdue');
    return attentionAll;
  }, [attentionFilter, attentionAll, attentionOverdue]);

  const rows = useMemo<Row[]>(() => {
    if (isAttentionMode) {
      return attentionItems.map((item) => ({
        installment: item.installment,
        contractLabel: item.contract
          ? `${item.contract.institution.name} · #${item.contract.contractNumber}`
          : undefined,
      }));
    }
    return contractInstallments
      .filter((i) => i.status !== 'paid')
      .sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
      )
      .map((installment) => ({ installment }));
  }, [isAttentionMode, attentionItems, contractInstallments]);

  const preselected = useRef(false);
  useEffect(() => {
    if (!isAttentionMode || !canPay || preselected.current || rows.length === 0)
      return;
    preselected.current = true;
    setSelectedIds(rows.map((r) => r.installment.id));
  }, [isAttentionMode, canPay, rows]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const open = new Set(rows.map((r) => r.installment.id));
      const next = prev.filter((sid) => open.has(sid));
      return next.length === prev.length ? prev : next;
    });
  }, [rows]);

  const overdueIds = useMemo(
    () =>
      rows
        .filter((r) => r.installment.status === 'overdue')
        .map((r) => r.installment.id),
    [rows],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedSet.has(r.installment.id)),
    [rows, selectedSet],
  );

  const selectedTotal = useMemo(
    () => selectedRows.reduce((sum, r) => sum + r.installment.amount, 0),
    [selectedRows],
  );

  const headerLine = useMemo(() => {
    if (!isAttentionMode) {
      return contract
        ? `${contract.institution.name} · #${contract.contractNumber}`
        : '';
    }
    const contractIds = new Set(rows.map((r) => r.installment.contractId));
    const label =
      contractIds.size === 1
        ? (rows[0]?.contractLabel ?? 'Parcelas em atenção')
        : `${contractIds.size} contratos`;
    return `${label} · parcelas em atenção`;
  }, [isAttentionMode, contract, rows]);

  const toggle = (installmentId: string) =>
    setSelectedIds((prev) =>
      prev.includes(installmentId)
        ? prev.filter((sid) => sid !== installmentId)
        : [...prev, installmentId],
    );

  const submit = async (): Promise<boolean> => {
    if (isAttentionMode) {
      const byContract = new Map<
        string,
        { contractId: string; installmentIds: string[]; label?: string }
      >();
      selectedRows.forEach((row) => {
        const key = row.installment.contractId;
        const group = byContract.get(key);
        if (group) {
          group.installmentIds.push(row.installment.id);
          return;
        }
        byContract.set(key, {
          contractId: key,
          installmentIds: [row.installment.id],
          label: row.contractLabel,
        });
      });
      return dispatch(
        markInstallmentsPaidByContract({ groups: [...byContract.values()] }),
      ).unwrap();
    }
    return dispatch(
      markInstallmentsPaid({
        contractId: id as string,
        installmentIds: selectedIds,
      }),
    ).unwrap();
  };

  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    modalStatusService.show({
      type: 'alert',
      icon: 'icon-circle-money',
      title:
        count === 1
          ? 'Registrar pagamento de 1 parcela?'
          : `Registrar pagamento de ${count} parcelas?`,
      description: `Cada parcela será registrada com o valor dela mesma, totalizando ${formatCentsToBRL(
        selectedTotal,
      )}. Para registrar um valor diferente, abra a parcela e defina o valor.`,
      textFirstButton: 'Confirmar',
      textSecondButton: 'Cancelar',
      showFirstButton: true,
      showSecondButton: true,
      onClickFirstButton: async () => {
        modalStatusService.hide();
        const ok = await submit();
        if (ok) {
          setSelectedIds([]);
          navigate(isAttentionMode ? '/app/alerts/all' : `/app/contracts/${id}`, {
            replace: true,
          });
        }
      },
      onClickSecondButton: modalStatusService.hide,
      handleCloseModal: modalStatusService.hide,
    });
  };

  if (!isAttentionMode && !contract) {
    return (
      <Screen headerTitle="Dar baixa em parcelas">
        <EmptyState
          icon="icon-document"
          title="Contrato não encontrado"
          description="Esse contrato pode ter sido removido. Volte e tente novamente."
        />
      </Screen>
    );
  }

  return (
    <Screen headerTitle="Dar baixa em parcelas">
      <div className="pb-[120px] pt-medium">
        <Box className="rounded-[20px] bg-intense-01 border border-intense-03 p-medium">
          <Text className="text-bodyMD color-intense-06">{headerLine}</Text>
          <Text className="mt-smaller text-captionLG color-intense-06 uppercase">
            {`${selectedIds.length} de ${rows.length} selecionadas`}
          </Text>
          <Text className="mt-quark text-display1 color-complement-01">
            {formatCentsToBRL(selectedTotal)}
          </Text>
          <Text className="mt-quark text-captionMD color-intense-05">
            Cada parcela é registrada com o valor dela mesma.
          </Text>

          <div className="mt-smaller flex flex-row gap-quark">
            <button
              type="button"
              onClick={() => setSelectedIds(overdueIds)}
              disabled={overdueIds.length === 0}
              className={cn(
                'rounded-full border border-intense-03 px-smaller py-quark',
                overdueIds.length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-intense-02',
              )}
            >
              <Text className="text-captionMD color-complement-01">
                Todas vencidas
              </Text>
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(rows.map((r) => r.installment.id))}
              disabled={rows.length === 0}
              className="rounded-full border border-intense-03 px-smaller py-quark hover:bg-intense-02"
            >
              <Text className="text-captionMD color-complement-01">
                Todas em aberto
              </Text>
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={selectedIds.length === 0}
              className={cn(
                'rounded-full border border-intense-03 px-smaller py-quark',
                selectedIds.length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-intense-02',
              )}
            >
              <Text className="text-captionMD color-complement-01">Limpar</Text>
            </button>
          </div>
        </Box>

        <FeatureGate feature="installments_pay">
          <div className="mt-medium flex flex-col gap-smaller">
            {rows.length === 0 ? (
              <EmptyState
                icon="icon-circle-check"
                title="Nenhuma parcela em aberto"
                description={
                  isAttentionMode
                    ? 'Nenhuma parcela precisa de atenção agora.'
                    : 'Todas as parcelas desse contrato já foram pagas.'
                }
              />
            ) : (
              rows.map((row) => (
                <SelectableInstallmentRow
                  key={row.installment.id}
                  installment={row.installment}
                  contractLabel={row.contractLabel}
                  selected={selectedSet.has(row.installment.id)}
                  disabled={saving}
                  onToggle={() => toggle(row.installment.id)}
                />
              ))
            )}
          </div>
        </FeatureGate>
      </div>

      {rows.length > 0 && canPay ? (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-intense-03 bg-intense-01 px-small py-smaller safe-area-bottom">
          <div className="mx-auto w-full xl:max-w-[1200px]">
            <Button
              text={
                selectedIds.length === 1
                  ? 'Registrar pagamento de 1 parcela'
                  : `Registrar pagamento de ${selectedIds.length} parcelas`
              }
              rightIcon="icon-check"
              onClick={handleConfirm}
              disabled={selectedIds.length === 0}
              loading={saving}
              fullWidth
            />
          </div>
        </div>
      ) : null}
    </Screen>
  );
}
